import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import { bindActionCreators } from 'redux';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import LoaderAction from 'yoda-site-components/lib/actions/LoadingAction';
import { getSelectedColor } from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import {
    miniPDPOnShopTheLookOpen,
    miniPDPOnClose,
} from 'yoda-site-components/lib/actions/MiniPDPAction';
import ScriptLoader from 'yoda-core-components/lib/helpers/ScriptLoader/ScriptLoader';
import {
    selectPreferences,
    selectFeatureFlags,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import { updateLastSelectedColor } from 'yoda-site-components/lib/actions/ShopTheLookAction';
import * as styles from './MixandMatch.css';
import {
    shopTheLookLoadSuccess,
    shopTheLookMiniPDPClickEvent,
    shopThisLookClickEvent,
} from '../../actions/AnalyticsAction';
import {
    MIX_AND_MATCH,
    MIX_AND_MATCH_StyliticsMarkup,
    MIX_AND_MATCH_StyliticsMarkupComposer,
} from './MixAndMatchConstants';

const cx = classNames.bind(styles);

export class MixandMatch extends Component {
    static propTypes = {
        productDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        productPrices: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        selectedColor: PropTypes.string,
        lastSelectedColor: PropTypes.string,
        outfitsData: PropTypes.func,
        jumpLink: PropTypes.func,
        actions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        miniPDPSTL: PropTypes.string,
        deviceType: PropTypes.object,
        preferences: PropTypes.oneOfType([PropTypes.object]),
        miniPDP: PropTypes.oneOfType([PropTypes.object]),
        featureFlags: PropTypes.oneOfType([PropTypes.object]),
    };

    static defaultProps = {
        productDetails: {},
        productPrices: [],
        selectedColor: '',
        lastSelectedColor: '',
        outfitsData: null,
        jumpLink: null,
        actions: {},
        miniPDPSTL: 'close',
        deviceType: {},
        preferences: {},
        miniPDP: {},
        featureFlags: {},
    };

    constructor(props) {
        super(props);
        this.styliticsMandMWidgetObj = null;
        this.state = {
            scriptLoaded: false,
            outfitsFromWidget: [],
            hideLoader: false,
            jumpLinkImage: null,
        };
        this.stylisticsRef = React.createRef();
        this.jumpLinkImg = null;
        this.observerActive = false;
    }

    observer =
        !__SERVER__ &&
        new MutationObserver(() => {
            if (this.stylisticsRef.current) {
                const {
                    featureFlags: { enableStyliticsComposerGrid = false } = {},
                    deviceType,
                } = this.props;
                const query = enableStyliticsComposerGrid
                    ? '.stylitics-ui-image'
                    : deviceType?.isDesktop
                    ? '.stylitics-moodboard-base .stylitics-moodboard-widget-container .stylitics-moodboard-anchor-product-wrap .stylitics-moodboard-anchor-product-image'
                    : '.stylitics-moodboard-base .stylitics-moodboard-anchor-product-wrap .stylitics-moodboard-anchor-product-image';
                const images = this.stylisticsRef.current.querySelectorAll(query);
                /* istanbul ignore next */
                if (images.length > 0) {
                    this.setState({ jumpLinkImage: images });
                    this.observer?.disconnect();
                    this.observerActive = false;
                }
            }
        });

    componentDidMount() {
        this.loadScript();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const {
            // miniPDP: { miniPDPSTLATC = false },
            actions,
        } = this.props;

        // TOFPMC-2853 - prevent rerunning logic if props have not changed
        const newMiniPDPState = nextProps?.miniPDP?.miniPDPSTL;
        const currentMiniPDPState = this.props?.miniPDP?.miniPDPSTL;
        if (newMiniPDPState !== currentMiniPDPState) {
            window.history.pushState(null, null, window.location.href);
            if (newMiniPDPState === 'open') {
                window.onpopstate = function () {
                    actions.miniPDPOnClose();
                };
            }
            if (newMiniPDPState === 'close') {
                window.onpopstate = function () {
                    window.history.back();
                };
            }
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.selectedColor && this.props.selectedColor !== prevProps.selectedColor) {
            this.refreshContent();
        }
        this.getPriceData();
        this.DOMManipulations();
        this.refreshJumplink();

        const { outfitsFromWidget } = this.state;
        const jumplinkElement = document.getElementById('stylitics-jumplink');
        const widgetContainer =
            document.getElementById('stylitics-widget-container') ||
            document.getElementById('stylitics-composer-container');

        // Stop observer if jumplink and widget are working
        if (jumplinkElement && widgetContainer && this.observerActive) {
            this.observer?.disconnect();
            this.observerActive = false;
            return;
        }

        if (
            outfitsFromWidget &&
            Object.keys(outfitsFromWidget).length > 0 &&
            !this.observerActive &&
            this.stylisticsRef.current
        ) {
            this.observer?.observe(this.stylisticsRef.current, {
                childList: true,
                subtree: true,
            });
            this.observerActive = true;
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const { selectedColor, productPrices } = this.props;
        const { outfitsFromWidget, hideLoader, jumpLinkImage } = this.state;

        if (selectedColor && selectedColor !== nextProps.selectedColor) {
            return true;
        }

        if (nextState.outfitsFromWidget !== outfitsFromWidget) {
            return true;
        }

        if (nextState.hideLoader !== hideLoader) {
            return true;
        }

        /* istanbul ignore next */
        if (nextState.jumpLinkImage !== jumpLinkImage) {
            this.props.jumpLink(nextState.jumpLinkImage);
            return true;
        }

        if (nextProps.productPrices !== productPrices) {
            return true;
        }

        return false;
    }

    loadScript = () => {
        const {
            preferences,
            featureFlags: { enableStyliticsComposerGrid = false } = {},
        } = this.props;
        const stylisticsURL = enableStyliticsComposerGrid
            ? get(preferences, 'mixandMatch.mnmScriptComposerURL', '')
            : get(preferences, 'mixandMatch.mnmScriptURL', '');
        const stylisticsProps = {
            src: stylisticsURL,
            async: true,
            defer: false,
            onSuccess: () => {
                enableStyliticsComposerGrid
                    ? this.initiateMixandMatchComposerGridFeature()
                    : this.initiateMixandMatchFeature();
            },
        };
        ScriptLoader.load(stylisticsProps);
        this.setState({ scriptLoaded: true });
    };

    DOMManipulations = () => {
        /* istanbul ignore next */
        if (this.stylisticsRef) {
            const cartBlock =
                this.stylisticsRef.current &&
                this.stylisticsRef.current.querySelectorAll(
                    `.stylitics-moodboard-item .stylitics-moodboard-item-info`
                );
            const cartIcon = `<span aria-label="cartIconBlock" class=${styles.cartIcon}> ${MIX_AND_MATCH.cartIcon} </span>`;
            if (!isEmpty(cartBlock)) {
                cartBlock.forEach((element) => {
                    const cartIconBlock = element.querySelectorAll(`[aria-label*="cartIconBlock"]`);
                    if (isEmpty(cartIconBlock)) {
                        element.insertAdjacentHTML('beforeend', cartIcon);
                    } else {
                        cartIconBlock[0].remove();
                        element.insertAdjacentHTML('beforeend', cartIcon);
                    }
                });
            }
        }
    };

    isCollection = () => {
        const { productDetails } = this.props;
        const collections = get(productDetails, 'collection', []);
        const collectionItem = collections && Object.keys(collections).length > 0;
        return collectionItem;
    };

    refreshJumplink = () => {
        const jumplinkElement = document.getElementById('stylitics-jumplink');
        const titleElement = document.getElementById('widget-title');
        const { outfitsFromWidget } = this.state;
        const hasOutfits = outfitsFromWidget && Object.keys(outfitsFromWidget).length > 0;

        if (jumplinkElement && titleElement) {
            jumplinkElement.style.display = hasOutfits ? 'block' : 'none';
            titleElement.style.display = hasOutfits ? 'block' : 'none';
        }
    };

    /* istanbul ignore next */
    itemPricingOverride = (data) => {
        const { item } = data;
        if (item.promotions.includes('with_code') && item.formattedSalePrice) {
            const text = document.createElement('span');
            text.classList.add('t-red');
            text.style.marginLeft = '2px';
            text.innerHTML = ' with code';
            const priceContainer = data.element.querySelector('.stylitics-ui-price');
            const salePrice = priceContainer.querySelector('.stylitics-item-sale-price');
            salePrice.classList.add('t-red');
            salePrice.append(text);
        }
    };

    /* istanbul ignore next */
    initiateMixandMatchFeature = () => {
        const { scriptLoaded, outfitsFromWidget } = this.state;
        const { selectedColor, actions } = this.props;
        /* istanbul ignore next */
        if (scriptLoaded) {
            let selectPrevColor = '';
            const opts = this.getOptionsForMixandMatch();
            this.styliticsMandMWidgetObj = new StyliticsMoodboardWidget(
                'jcpenney_home',
                'stylitics-widget-container',
                opts
            );
            this.styliticsMandMWidgetObj.on('load', 'widget', (outfits) => {
                this.setState({
                    hideLoader: true,
                });
                if (outfits && Object.keys(outfits).length > 0) {
                    window && actions.shopTheLookLoadSuccess(window.digitalData);
                    this.isCollection() && this.props.outfitsData(false);
                    this.setState(
                        {
                            outfitsFromWidget: outfits,
                        },
                        () => {
                            this.refreshJumplink();
                        }
                    );
                    selectPrevColor = get(outfits, 'bundles[0].primary_remote_id', '');
                } else {
                    selectPrevColor !== '' && this.refreshContent(selectPrevColor);
                    this.isCollection() && this.props.outfitsData(true);
                    this.setState({
                        outfitsFromWidget: [],
                    });
                }
            });

            this.styliticsMandMWidgetObj.override('click', 'item', ({ item, bundle }) => {
                this.onMixandMatchItemClick(item, bundle);
            });
            this.styliticsMandMWidgetObj.start();
        }
    };

    initiateMixandMatchComposerGridFeature = async () => {
        const { scriptLoaded } = this.state;
        const { selectedColor, actions } = this.props;
        if (scriptLoaded && typeof StyliticsComposer !== 'undefined') {
            let selectPrevColor = '';
            this.composer = await StyliticsComposer.initialize({
                client: 'jcpenney_home',
                target: 'stylitics-composer-container',
                config: {
                    modules: [
                        {
                            type: 'grid',
                            params: {
                                api: {
                                    item_number: this.getItemNumber(selectedColor),
                                    min: 3,
                                    max: 6,
                                },
                                display: {
                                    showMobileArrows: true,
                                    nonEditorialImages: true,
                                    itemInfoStatic: true,
                                    typeScaledGrid: true,
                                    maxGridSize: 5,
                                },
                                text: {
                                    counterHeading: 'Room',
                                },
                            },
                            /* istanbul ignore next */
                            on: {
                                'mount.item': (data) => this.itemPricingOverride(data),
                                'load.data': ({ outfits }) => {
                                    this.setState({ hideLoader: true });
                                    if (outfits && Object.keys(outfits).length > 0) {
                                        window &&
                                            actions.shopTheLookLoadSuccess(window.digitalData);
                                        this.isCollection() && this.props.outfitsData?.(false);
                                        this.setState({ outfitsFromWidget: outfits }, () => {
                                            this.refreshJumplink();
                                            if (this.stylisticsRef.current) {
                                                const images = this.stylisticsRef.current.querySelectorAll(
                                                    '.stylitics-ui-image'
                                                );
                                                if (images.length > 0) {
                                                    this.setState({ jumpLinkImage: images });
                                                }
                                            }
                                        });
                                        selectPrevColor = get(
                                            outfits,
                                            'bundles[0].primary_remote_id',
                                            ''
                                        );
                                    } else {
                                        if (selectPrevColor) this.refreshContent(selectPrevColor);
                                        this.isCollection() && this.props.outfitsData?.(true);
                                        this.setState(
                                            { outfitsFromWidget: [], jumpLinkImage: null },
                                            () => {
                                                this.refreshJumplink();
                                                this.props.jumpLink?.(null);
                                            }
                                        );
                                    }
                                },
                            },
                            override: {
                                'click.item': ({ item, outfit }) => {
                                    this.onMixandMatchItemClick(item, outfit);
                                },
                            },
                        },
                    ],
                },
            });
            await this.composer.start();
        }
    };

    getOptionsForMixandMatch = () => {
        const opts = {
            api: {
                item_number: this.getItemNumber(this.props.selectedColor),
            },
            text: {
                bundleCounterLabel: 'Room',
            },
            customer: {
                identifier: 'jcpenney',
            },
            display: {
                moodboardImageType: 'non-editorial',
                mobileItemClickOverrideTarget: 'click-item',
                cssOverrides: `
                .stylitics-swap-icon{
                padding:0!important;
                background-color:transparent!important;
                width: 28px!important;
                height: 28px!important;
                right: unset!important;
                left: 6px!important;
                bottom: 6px!important;
                background-size: 100%!important;
                background-image: url('https://web-assets.stylitics.com/client-assets/swap-icon-jcp.svg')!important;
                }
                .stylitics-moodboard-item-info>button.stylitics-swap-icon{
                opacity: 0.8!important;
                }`,
            },
            price: {
                salesPriceStyle: 'sales-price-only',
            },
            navigation: {
                clickItemLink: '',
            },
        };
        return opts;
    };

    getItemNumber = (selectedColor) => {
        const { productDetails } = this.props;
        /* istanbul ignore next */
        if (selectedColor && selectedColor.includes(productDetails.id)) {
            return selectedColor;
        }
        const selectedColorName = selectedColor || productDetails.ppFeatureColor;
        const itemNumber = selectedColorName
            ? `${productDetails.id}_${selectedColorName.replace(/ /g, '-').toLowerCase()}`
            : '';
        return itemNumber;
    };

    onMixandMatchItemClick = (ppId, bundle) => {
        const { actions, featureFlags: { enableStyliticsComposerGrid = false } = {} } = this.props;
        /* istanbul ignore next */
        const skuData = ppId && ppId.remote_id;
        const data = enableStyliticsComposerGrid
            ? this.stylisticsRef &&
              this.stylisticsRef.current &&
              this.stylisticsRef.current.querySelectorAll(`[data-stylitics-id*="${ppId.item_id}"]`)
            : this.stylisticsRef &&
              this.stylisticsRef.current &&
              this.stylisticsRef.current.querySelectorAll(
                  `[data-stylitics-item-id*="${ppId.item_id}"] .stylitics-moodboard-item-info a`
              );
        if (!isEmpty(data)) {
            data[0].removeAttribute('href');
        }
        /* istanbul ignore next */
        if (!isEmpty(skuData)) {
            const options = {
                ppId: skuData.split('_')[0],
                skuId:
                    (ppId &&
                        !isEmpty(ppId.other_client_item_ids) &&
                        !isEmpty(ppId.other_client_item_ids.sku_nbrs) &&
                        ppId.other_client_item_ids.sku_nbrs[0]) ||
                    '',
            };
            const customEvent = document.createEvent('CustomEvent');
            customEvent.initCustomEvent('miniPDP.open', true, true, options);
            actions.miniPDPOnShopTheLookOpen();
            actions.shopTheLookMiniPDPClickEvent({
                moduleName: MIX_AND_MATCH.mnmHeader.toLowerCase(),
                shopTheLookID: bundle.id,
                shopTheLookPPID: skuData.split('_')[0],
            });
            document.dispatchEvent(customEvent);
        }
    };

    constructPPid = (data) => {
        const ProductsList = new Set();
        if (data && data.length) {
            data.forEach((element) => {
                element &&
                    element.items &&
                    element.items.forEach((subelement) => {
                        ProductsList.add(subelement.remote_id.split('_')[0]);
                    });
            });
        }
        const productIds = [...ProductsList];
        return productIds;
    };

    filterData = (data, ppId) => {
        let productIds;
        /* istanbul ignore next */
        if (data && data.length) {
            data.forEach((element) => {
                productIds = element.items.filter(
                    (subelement) => subelement.remote_id.split('_')[0] === ppId
                );
            });
        }
        return productIds;
    };

    /* istanbul ignore next */
    getPriceData = () => {
        const { outfitsFromWidget } = this.state;
        const { productDetails } = this.props;
        const promoCodeText = ' with code';
        outfitsFromWidget?.bundles?.forEach((bundle) => {
            bundle.items?.forEach((item) => {
                if (item.promotions.includes('with_code')) {
                    const text = document.createElement('span');
                    text.classList.add('promotional-text');
                    text.innerHTML = promoCodeText;
                    const priceContainer = document.querySelectorAll(
                        `#stylitics-widget-container .stylitics-moodboard-widget-container [data-stylitics-item-id*="${item.item_id}"] .stylitics-moodboard-item-info .stylitics-item-price .stylitics-item-regular-price`
                    );
                    priceContainer.forEach((price) => {
                        if (price) {
                            const ispromotionaltextavailablee = price?.querySelectorAll(
                                '.promotional-text'
                            );
                            if (isEmpty(ispromotionaltextavailablee)) {
                                if (price) {
                                    price.append(text);
                                    price.classList.add('promotional-sale-price');
                                }
                            }
                        }
                    });
                    if (item.product_id === productDetails.base.id) {
                        const priceContainermain = document.querySelectorAll(
                            '#stylitics-widget-container .stylitics-moodboard-base .stylitics-moodboard-anchor-product-wrap .stylitics-moodboard-anchor-product-price'
                        );
                        priceContainermain.forEach((price) => {
                            const promotionalPricemain = price.querySelector(
                                '.stylitics-ui-regular-price'
                            );
                            const ispromotionaltextavailable = price.querySelectorAll(
                                '.promotional-text'
                            );
                            if (isEmpty(ispromotionaltextavailable)) {
                                if (promotionalPricemain) {
                                    promotionalPricemain.append(text);
                                    promotionalPricemain.classList.add('promotional-sale-price');
                                }
                            }
                        });
                    }
                }
            });
        });
    };

    refreshContent = async (color = this.props.selectedColor) => {
        const { scriptLoaded } = this.state;
        const { featureFlags: { enableStyliticsComposerGrid = false } = {} } = this.props;
        if (scriptLoaded) {
            const itemNumber = this.getItemNumber(color);
            if (enableStyliticsComposerGrid && this.composer) {
                await this.composer.refresh({
                    sharedParams: { api: { item_number: itemNumber } },
                });
            } else if (this.styliticsMandMWidgetObj) {
                this.styliticsMandMWidgetObj.refresh({ api: { item_number: itemNumber } });
            }
            this.refreshJumplink();
        }
    };

    loadContent = (outfitsFromWidget) => {
        const { hideLoader } = this.state;
        const { featureFlags: { enableStyliticsComposerGrid = false } = {} } = this.props;
        const markup = enableStyliticsComposerGrid
            ? MIX_AND_MATCH_StyliticsMarkupComposer
            : MIX_AND_MATCH_StyliticsMarkup;

        const mixandMatchBlock = (
            <div>
                {!hideLoader && (
                    <div className={[cx('placeHolder', 'm-auto', 'mt-4')].join(' ')}>
                        <Loader />
                    </div>
                )}
                <div className={enableStyliticsComposerGrid && dt(['mb-6'])}>
                    <h4
                        id="widget-title"
                        className={
                            enableStyliticsComposerGrid
                                ? dt(['text-xLg', 'text-center', 'font-open-sans-semibold', 'pb-3'])
                                : dt([
                                      'text-center',
                                      'font-open-sans-semibold',
                                      '!leading-8',
                                      'lg:text-xLg',
                                      'sm:text-lg',
                                      'pb-3',
                                  ])
                        }
                    >
                        {outfitsFromWidget &&
                            Object.keys(outfitsFromWidget).length > 0 &&
                            MIX_AND_MATCH.mnmHeader}
                    </h4>
                </div>
                <div
                    id={
                        enableStyliticsComposerGrid
                            ? 'stylitics-composer-container'
                            : 'stylitics-widget-container'
                    }
                    dangerouslySetInnerHTML={{ __html: markup }}
                    ref={this.stylisticsRef}
                />
            </div>
        );
        return mixandMatchBlock;
    };

    render() {
        const { outfitsFromWidget } = this.state;
        return <div>{this.loadContent(outfitsFromWidget)}</div>;
    }
}

const mapStateToProps = (state) => ({
    selectedColor: getSelectedColor(false)(state),
    preferences: selectPreferences(state),
    productPrices: state.shopTheLook ? state.shopTheLook.productPrices : {},
    lastSelectedColor: state.shopTheLook ? state.shopTheLook.lastSelectedColor : '',
    miniPDPSTL: state.miniPDP ? state.miniPDP.miniPDPSTL : '',
    miniPDP: state.miniPDP ? state.miniPDP : {},
    deviceType: state.context ? state.context.deviceType : {},
    featureFlags: selectFeatureFlags(state),
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            ...LoaderAction,
            updateLastSelectedColor,
            shopTheLookLoadSuccess,
            shopThisLookClickEvent,
            shopTheLookMiniPDPClickEvent,
            miniPDPOnShopTheLookOpen,
            miniPDPOnClose,
        },
        dispatch
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(MixandMatch);
